import { Link, useParams } from "react-router-dom"
import { BenefitSummaryStats } from "../../components/BenefitSummaryStats.js"
import { BenefitTiersTable } from "../../components/BenefitTiersTable.js"
import { Breadcrumbs } from "../../components/Breadcrumbs.js"
import { CategoryBadge } from "../../components/CategoryBadge.js"
import { RecordDeadlineRibbon } from "../../components/RecordDeadlineRibbon.js"
import { SiteFooter } from "../../components/SiteFooter.js"
import { SiteHeader } from "../../components/SiteHeader.js"
import { TagBadge } from "../../components/TagBadge.js"
import { googleSearchUrl, yahooFinanceJpUrl } from "../../lib/financeLinks.js"
import { useAllBenefits } from "../../lib/useAllBenefits.js"
import textLinkStyles from "../../styles/textLink.module.css"
import styles from "./CompanyPage.module.css"
import { SimilarBenefitsSection } from "./SimilarBenefitsSection.js"

export function CompanyPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = useAllBenefits()

  const benefit = data?.find((b) => b.id === id)

  return (
    <div className={styles.page}>
      <SiteHeader />
      <Breadcrumbs
        className={styles.breadcrumbs}
        items={
          benefit
            ? [{ label: "トップ", href: "/" }, { label: benefit.name }]
            : [{ label: "トップ", href: "/" }, { label: "企業が見つかりません" }]
        }
      />

      <main className={styles.main}>
        {isLoading && <div className={styles.loading}>読み込み中...</div>}
        {error && <div className={styles.error}>データの取得に失敗しました。</div>}
        {!isLoading && !error && !benefit && (
          <div className={styles.notFound}>
            <p>企業が見つかりませんでした。</p>
            <Link to="/">一覧に戻る</Link>
          </div>
        )}

        {benefit && (
          <article className={styles.article}>
            <RecordDeadlineRibbon benefit={benefit} />
            <div className={styles.companyHeader}>
              <div className={styles.companyMeta}>
                <span className={styles.code}>{benefit.id}</span>
                <span className={styles.industry}>| {benefit.industry}</span>
              </div>
              <h1 className={styles.companyName}>{benefit.name}</h1>
            </div>

            {benefit.businessDescription && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>事業内容</h2>
                <p className={styles.description}>{benefit.businessDescription}</p>
              </section>
            )}

            <div className={styles.categories}>
              {benefit.categories.map((cat) => (
                <CategoryBadge key={cat} category={cat} />
              ))}
              {benefit.tags.map((tag) => (
                <TagBadge key={tag} tag={tag} />
              ))}
            </div>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>優待概要</h2>
              <p className={styles.description}>{benefit.description}</p>
            </section>

            {benefit.tiers.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>株数別優待内容</h2>
                <BenefitTiersTable tiers={benefit.tiers} className={styles.tiersTable} />
              </section>
            )}

            <BenefitSummaryStats benefit={benefit} />

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>関連リンク</h2>
              <div className={textLinkStyles.list}>
                <a
                  href={benefit.officialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={textLinkStyles.link}
                >
                  公式ページで確認する ↗
                </a>
                <a
                  href={googleSearchUrl(benefit.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={textLinkStyles.link}
                >
                  優待ブログ検索 ↗
                </a>
                <a
                  href={yahooFinanceJpUrl(benefit.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={textLinkStyles.link}
                >
                  Yahoo!ファイナンスで株価を見る ↗
                </a>
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>基本情報</h2>
              <dl className={styles.dl}>
                <dt>最低株数</dt>
                <dd>{benefit.minimumShares.toLocaleString()}株</dd>
                {benefit.minimumBenefitValue != null && (
                  <>
                    <dt>優待金額目安</dt>
                    <dd>{benefit.minimumBenefitValue.toLocaleString()}円相当〜</dd>
                  </>
                )}
                {benefit.holdingYears.length > 0 && (
                  <>
                    <dt>長期保有条件</dt>
                    <dd>{benefit.holdingYears.map((y) => `${y}年以上`).join("・")}から特典</dd>
                  </>
                )}
              </dl>
            </section>

            <div className={styles.footer}>
              <span className={styles.updated}>
                更新: {new Date(benefit.updatedAt).toLocaleDateString("ja-JP")}
              </span>
            </div>
          </article>
        )}
      </main>

      {benefit && data && <SimilarBenefitsSection benefit={benefit} allBenefits={data} />}

      <SiteFooter />
    </div>
  )
}
